// ===== FILE: src/services/contractorService.ts =====
import { Contractor } from "../models/Contractor.interface";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3001/api";

export interface ContractorFilters {
  category?: string;
  city?: string;
  state?: string;
  search?: string;
  page?: number;
  limit?: number;
  minRating?: number;
  withReviews?: boolean;
  orderBy?: "rating" | "business_name" | "review_count" | "created_at";
  orderDirection?: "ASC" | "DESC";
}

export interface ContractorResponse {
  success: boolean;
  data: Contractor[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
  };
  filters?: any;
  error?: string;
  message?: string;
}

export interface LocationWithCount {
  state: string;
  city?: string;
  count: number;
}

export interface CategoryWithCount {
  category: string;
  count: number;
}

export interface ContractorStats {
  totalContractors: number;
  totalStates: number;
  totalCities: number;
  totalCategories: number;
  avgRating: number;
}

class ContractorAPIService {
  private cache = new Map<
    string,
    { data: any; timestamp: number; ttl: number }
  >();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Cache management
   */
  private getCacheKey(endpoint: string, params?: any): string {
    return `${endpoint}:${JSON.stringify(params || {})}`;
  }

  private setCache(key: string, data: any, ttl = this.DEFAULT_TTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  private getCache(key: string): any | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    if (Date.now() - cached.timestamp > cached.ttl) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  private async fetchWithCache<T>(
    endpoint: string,
    params?: any,
    ttl = this.DEFAULT_TTL
  ): Promise<T> {
    const cacheKey = this.getCacheKey(endpoint, params);
    const cached = this.getCache(cacheKey);

    if (cached) {
      return cached as T; // Add proper casting here
    }

    const url = new URL(`${API_BASE_URL}${endpoint}`);
    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          url.searchParams.append(key, params[key].toString());
        }
      });
    }

    const response = await fetch(url.toString());

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || "API request failed");
    }

    this.setCache(cacheKey, data, ttl);
    return data as T; // Add proper casting here
  }

  /**
   * Get contractors with filtering
   */
  async getContractors(
    filters: ContractorFilters = {}
  ): Promise<ContractorResponse> {
    return this.fetchWithCache("/contractors", filters);
  }

  /**
   * Get single contractor by ID
   */
  async getContractorById(
    contractorId: string
  ): Promise<{ success: boolean; data: Contractor }> {
    return this.fetchWithCache(
      `/contractors/${contractorId}`,
      null,
      10 * 60 * 1000
    ); // 10 minute cache
  }

  /**
   * Search contractors with full-text search
   */
  async searchContractors(
    searchTerm: string,
    options: { page?: number; limit?: number } = {}
  ): Promise<ContractorResponse> {
    return this.fetchWithCache(
      `/contractors/search/${encodeURIComponent(searchTerm)}`,
      options
    );
  }

  /**
   * Get contractors by location
   */
  async getContractorsByLocation(
    state: string,
    city?: string,
    options: { page?: number; limit?: number } = {}
  ): Promise<ContractorResponse> {
    const params = { city, ...options };
    return this.fetchWithCache(`/contractors/by-location/${state}`, params);
  }

  /**
   * Get contractors by category
   */
  async getContractorsByCategory(
    category: string,
    options: {
      state?: string;
      city?: string;
      page?: number;
      limit?: number;
    } = {}
  ): Promise<ContractorResponse> {
    return this.fetchWithCache(`/contractors/by-category/${category}`, options);
  }

  /**
   * Get contractors by category and location
   */
  async getContractorsByCategoryAndLocation(
    category: string,
    state: string,
    city: string,
    options: { page?: number; limit?: number } = {}
  ): Promise<ContractorResponse> {
    const params = { state, city, ...options };
    return this.fetchWithCache(`/contractors/by-category/${category}`, params);
  }

  /**
   * Get all locations with contractor counts
   */
  async getLocationsWithCounts(): Promise<{
    success: boolean;
    data: LocationWithCount[];
  }> {
    return this.fetchWithCache(
      "/contractors/locations/all",
      null,
      30 * 60 * 1000
    ); // 30 minute cache
  }

  /**
   * Get all categories with contractor counts
   */
  async getCategoriesWithCounts(): Promise<{
    success: boolean;
    data: CategoryWithCount[];
  }> {
    return this.fetchWithCache(
      "/contractors/categories/all",
      null,
      30 * 60 * 1000
    ); // 30 minute cache
  }

  /**
   * Get contractor statistics
   */
  async getStatistics(): Promise<{ success: boolean; data: ContractorStats }> {
    return this.fetchWithCache(
      "/contractors/stats/overview",
      null,
      60 * 60 * 1000
    ); // 1 hour cache
  }

  /**
   * Clear cache (useful for refreshing data)
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Clear specific cache entries
   */
  clearCacheByPattern(pattern: string): void {
    Array.from(this.cache.keys()).forEach((key) => {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    });
  }

  /**
   * Get popular locations with contractor counts
   */
  async getPopularLocations(
    options: { limit?: number } = {}
  ): Promise<{ success: boolean; data: any[] }> {
    try {
      const { limit = 20 } = options;
      const response = await this.fetchWithCache<{
        success: boolean;
        data: any[];
      }>(`/contractors/popular-locations?limit=${limit}`, null, 5 * 60 * 1000);
      return response;
    } catch (error) {
      console.error("Error fetching popular locations:", error);
      // Return fallback data
      return {
        success: true,
        data: [
          { state: "CA", city: "Los Angeles", count: 245 },
          { state: "TX", city: "Houston", count: 189 },
          { state: "FL", city: "Miami", count: 156 },
          { state: "NY", city: "New York", count: 203 },
          { state: "VA", city: "Virginia Beach", count: 78 },
        ],
      };
    }
  }

  /**
   * Get locations by category
   */
  async getLocationsByCategory(
    category: string,
    options: { limit?: number } = {}
  ): Promise<{ success: boolean; data: any[] }> {
    try {
      const { limit = 50 } = options;
      const response = await this.fetchWithCache<{
        success: boolean;
        data: any[];
      }>(
        `/contractors/locations-by-category/${encodeURIComponent(
          category
        )}?limit=${limit}`,
        null,
        10 * 60 * 1000
      );
      return response;
    } catch (error) {
      console.error("Error fetching locations by category:", error);
      return { success: false, data: [] };
    }
  }
}

// Export singleton instance
export const contractorAPI = new ContractorAPIService();

// Legacy compatibility functions for existing components
export const getContractors = (filters: ContractorFilters = {}) =>
  contractorAPI.getContractors(filters);

export const getContractorById = (contractorId: string) =>
  contractorAPI.getContractorById(contractorId);

export const searchContractors = (searchTerm: string, options = {}) =>
  contractorAPI.searchContractors(searchTerm, options);

export const getContractorsByLocation = (
  state: string,
  city?: string,
  options = {}
) => contractorAPI.getContractorsByLocation(state, city, options);

export const getContractorsByCategory = (category: string, options = {}) =>
  contractorAPI.getContractorsByCategory(category, options);

export const getLocationsWithCounts = () =>
  contractorAPI.getLocationsWithCounts();

export const getCategoriesWithCounts = () =>
  contractorAPI.getCategoriesWithCounts();

export const getContractorStats = () => contractorAPI.getStatistics();
